import { useSyncExternalStore } from 'react';
import type { InventoryLocationSelection, InventoryVariboxSnapshot } from '@/data/inventoryUtils';
import type { VariboxStatusKey } from '@/data/variboxMockData';

export interface CompletedInventorySession {
  id: string;
  location: InventoryLocationSelection;
  locationLabel: string;
  startAtIso: string;
  endAtIso: string;
  user: string;
  expectedCount: number;
  foundCount: number;
  unexpectedCount: number;
  missingCount: number;
  status: 'completed';
  found: InventoryVariboxSnapshot[];
  unexpected: Array<InventoryVariboxSnapshot & { currentLocationLabel: string }>;
  missing: InventoryVariboxSnapshot[];
}

export type InventoryScanResult =
  | { type: 'found'; serialNumber: string; radexId: string; currentLocationLabel: string; status: VariboxStatusKey }
  | { type: 'unexpected'; serialNumber: string; radexId: string; currentLocationLabel: string; inventoryLocationLabel: string; status: VariboxStatusKey }
  | { type: 'duplicate'; serialNumber: string; radexId: string }
  | { type: 'unknown'; barcode: string };

interface ActiveInventorySession {
  id: string;
  location: InventoryLocationSelection;
  locationLabel: string;
  startAtIso: string;
  user: string;
  expected: InventoryVariboxSnapshot[];
  found: InventoryVariboxSnapshot[];
  unexpected: Array<InventoryVariboxSnapshot & { currentLocationLabel: string }>;
  scannedBySerial: Record<string, true>;
  lastScanResult: InventoryScanResult | null;
}

interface InventoryStoreState {
  activeSession: ActiveInventorySession | null;
  completedSessions: CompletedInventorySession[];
}

let inventoryStoreState: InventoryStoreState = {
  activeSession: null,
  completedSessions: [],
};

const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return inventoryStoreState;
}

function setLastScanResult(lastScanResult: InventoryScanResult) {
  const session = inventoryStoreState.activeSession;
  if (!session) return;

  inventoryStoreState = {
    ...inventoryStoreState,
    activeSession: { ...session, lastScanResult },
  };
}

export function useInventoryDemoState() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const startSession = (input: {
    location: InventoryLocationSelection;
    locationLabel: string;
    expected: InventoryVariboxSnapshot[];
    user: string;
  }) => {
    inventoryStoreState = {
      ...inventoryStoreState,
      activeSession: {
        id: `${input.location.rootId}-${input.location.locationId}-${Date.now()}`,
        location: input.location,
        locationLabel: input.locationLabel,
        startAtIso: new Date().toISOString(),
        user: input.user,
        expected: input.expected,
        found: [],
        unexpected: [],
        scannedBySerial: {},
        lastScanResult: null,
      },
    };

    notifyListeners();
  };

  const resetActiveSession = () => {
    inventoryStoreState = { ...inventoryStoreState, activeSession: null };
    notifyListeners();
  };

  const registerUnknownScan = (barcode: string) => {
    if (!inventoryStoreState.activeSession) {
      return null;
    }

    const result: InventoryScanResult = { type: 'unknown', barcode };
    setLastScanResult(result);
    notifyListeners();

    return result;
  };

  const registerScan = (input: {
    serialNumber: string;
    radexId: string;
    status: VariboxStatusKey;
    currentLocation: InventoryLocationSelection;
    currentLocationLabel: string;
  }) => {
    const session = inventoryStoreState.activeSession;

    if (!session) {
      return null;
    }

    if (session.scannedBySerial[input.serialNumber]) {
      const duplicateResult: InventoryScanResult = {
        type: 'duplicate',
        serialNumber: input.serialNumber,
        radexId: input.radexId,
      };
      setLastScanResult(duplicateResult);
      notifyListeners();
      return duplicateResult;
    }

    const baseSnapshot: InventoryVariboxSnapshot = {
      serialNumber: input.serialNumber,
      radexId: input.radexId,
      status: input.status,
      location: {
        rootId: input.currentLocation.rootId,
        locationId: input.currentLocation.locationId,
      },
      currentLocationLabel: input.currentLocationLabel,
      lastMovement: '',
    };

    const isExpected = input.currentLocation.locationId === session.location.locationId;

    let result: InventoryScanResult;

    if (isExpected) {
      result = {
        type: 'found',
        serialNumber: input.serialNumber,
        radexId: input.radexId,
        currentLocationLabel: input.currentLocationLabel,
        status: input.status,
      };
    } else {
      result = {
        type: 'unexpected',
        serialNumber: input.serialNumber,
        radexId: input.radexId,
        currentLocationLabel: input.currentLocationLabel,
        inventoryLocationLabel: session.locationLabel,
        status: input.status,
      };
    }

    inventoryStoreState = {
      ...inventoryStoreState,
      activeSession: {
        ...session,
        found: isExpected ? [...session.found, baseSnapshot] : session.found,
        unexpected: isExpected
          ? session.unexpected
          : [...session.unexpected, { ...baseSnapshot, currentLocationLabel: input.currentLocationLabel }],
        scannedBySerial: { ...session.scannedBySerial, [input.serialNumber]: true },
        lastScanResult: result,
      },
    };
    notifyListeners();

    return result;
  };

  const completeSession = () => {
    const session = inventoryStoreState.activeSession;

    if (!session) {
      return null;
    }

    const foundSerials = new Set(session.found.map((item) => item.serialNumber));
    const missing = session.expected.filter((item) => !foundSerials.has(item.serialNumber));

    const completed: CompletedInventorySession = {
      id: session.id,
      location: session.location,
      locationLabel: session.locationLabel,
      startAtIso: session.startAtIso,
      endAtIso: new Date().toISOString(),
      user: session.user,
      expectedCount: session.expected.length,
      foundCount: session.found.length,
      unexpectedCount: session.unexpected.length,
      missingCount: missing.length,
      status: 'completed',
      found: session.found,
      unexpected: session.unexpected,
      missing,
    };

    inventoryStoreState = {
      activeSession: null,
      completedSessions: [completed, ...inventoryStoreState.completedSessions],
    };
    notifyListeners();

    return completed;
  };

  return {
    activeSession: state.activeSession,
    completedSessions: state.completedSessions,
    startSession,
    resetActiveSession,
    registerUnknownScan,
    registerScan,
    completeSession,
  };
}
